import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Box,
  Chip,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ImageList,
  ImageListItem,
  Dialog,
  DialogContent,
  IconButton,
  Divider,
  Stack,
  Breadcrumbs,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip,
  Fade,
  Collapse,
} from "@mui/material";
import {
  RequestQuote as QuoteIcon,
  ContactPhone as ContactIcon,
  CalendarToday,
  Speed,
  LocationOn,
  Build,
  CheckCircle,
  Close,
  ZoomIn,
  PlayArrow,
  Share,
  Favorite,
  Map as MapIcon,
  Description as FileIcon,
  ArrowBack,
  Inventory,
  VideoLibrary,
  FavoriteBorder,
  WhatsApp,
  Facebook,
  Twitter,
  LinkedIn,
  ArrowBackIos,
  ArrowForwardIos,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Login as LoginIcon,
  Phone,
  Email,
  ReceiptLong,
  ExpandMore,
  ExpandLess,
  Info as InfoIcon,
} from "@mui/icons-material";
import ContactForm from "../../components/forms/ContactForm";
import QuoteForm from "../../components/forms/QuoteForm";
import { useSEO } from "../../hooks/useSEO";
import { useProductStructuredData } from "../../hooks/useStructuredData";
import placeholderImage from "../../assets/image-placeholder.webp";
import YoutubeEmbed from "../../components/common/YoutubeEmbed";
import productsService from "../../services/productsService";
import useNavigationMemory from "../../hooks/useNavigationMemory";
import { fixImageUrl, fixProductImages } from "../../utils/patchImages";
import { buildPriceBreakdownRows } from "../../utils/priceBreakdown";
import { FavoriteButton } from "../../components/common/FavoriteButton";
import LoginModal from "../../components/common/LoginModal";
import { useAuth } from "../../context/context";
import { CONTACT_INFO, locations } from "../../constants";
import CopyButton from "../../components/common/CopyButton";
import useResponsive from "../../hooks/useResponsive";
import SmartImage, { SmartCardMedia } from "../../components/common/SmartImage";

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { navigateToCatalog } = useNavigationMemory();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openContactForm, setOpenContactForm] = useState(false);
  const [openQuoteForm, setOpenQuoteForm] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(0);
  const [openMediaDialog, setOpenMediaDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [shareMenuAnchor, setShareMenuAnchor] = useState(null);
  const [shareSnackbar, setShareSnackbar] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [openLoginModal, setOpenLoginModal] = useState(false);
  const [openPriceBreakdownModal, setOpenPriceBreakdownModal] = useState(false);
  // 🖼️ Miniaturas reales de videos de Vimeo (keyed por URL), obtenidas vía oEmbed.
  const [vimeoThumbs, setVimeoThumbs] = useState({});
  const { isMobile, isTablet } = useResponsive();

  // 🛡️ Si el desglose existe pero todos sus campos son 0 (usuario ingresó 0
  // en todos los detalles), tratamos como si no hubiera desglose y usamos
  // el precio del lote.
  const hasValidPriceBreakdown = useMemo(() => {
    const pb = product?.priceBreakdown;
    if (!product?.hasPriceDetails || !pb) return false;
    const additional = Array.isArray(pb.additionalCosts)
      ? pb.additionalCosts.reduce((s, c) => s + Number(c?.value || 0), 0)
      : 0;
    return (
      Number(pb.netPrice || 0) > 0 ||
      Number(pb.totalPrice || 0) > 0 ||
      Number(pb.netPriceVat || 0) > 0 ||
      Number(pb.transferTax || 0) > 0 ||
      Number(pb.transferFee || 0) > 0 ||
      additional > 0
    );
  }, [product]);

  const hasLicensePlate = useMemo(() => {
    return !!product?.details?.patente;
  }, [product]);

  useEffect(() => {
    const handleOpenLoginModal = () => {
      setOpenLoginModal(true);
    };

    window.addEventListener("openLoginModal", handleOpenLoginModal);

    return () => {
      window.removeEventListener("openLoginModal", handleOpenLoginModal);
    };
  }, []);

  // 🖼️ Cargar las miniaturas REALES de los videos de Vimeo vía la API oEmbed.
  // Ojo: la CDN `i.vimeocdn.com/video/{id}` NO acepta el ID del video (devuelve
  // las barras de color de Vimeo). El thumbnail real (incluido uno custom subido
  // por el admin) solo se obtiene desde `thumbnail_url` del oEmbed.
  useEffect(() => {
    const videos = product?.videos || [];
    const vimeoUrls = videos
      .map((v) => v?.url)
      .filter((url) => url && url.includes("vimeo.com"));
    if (vimeoUrls.length === 0) return undefined;

    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        vimeoUrls.map(async (url) => {
          try {
            const res = await fetch(
              `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
                url,
              )}&width=640`,
            );
            if (!res.ok) return null;
            const json = await res.json();
            return json?.thumbnail_url ? [url, json.thumbnail_url] : null;
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      const map = {};
      results.forEach((entry) => {
        if (entry) map[entry[0]] = entry[1];
      });
      if (Object.keys(map).length > 0) {
        setVimeoThumbs((prev) => ({ ...prev, ...map }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [product?.videos]);

  const handleQuoteClick = () => {
    if (!user) {
      setOpenLoginModal(true);
    } else {
      setOpenQuoteForm(true);
    }
  };

  const handleShareClick = (event) => {
    setShareMenuAnchor(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareMenuAnchor(null);
  };

  const shareOptions = [
    {
      name: "WhatsApp",
      icon: <WhatsApp />,
      action: () => {
        const text = encodeURIComponent(
          `Mira este ${product.name} - ${product.formattedPrice} ${window.location.href}`,
        );
        window.open(`https://wa.me/?text=${text}`, "_blank");
        setShareMessage("Abriendo WhatsApp...");
        setShareSnackbar(true);
      },
    },
    {
      name: "Facebook",
      icon: <Facebook />,
      action: () => {
        const url = encodeURIComponent(window.location.href);
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${url}`,
          "_blank",
        );
        setShareMessage("Abriendo Facebook...");
        setShareSnackbar(true);
      },
    },
    {
      name: "Twitter",
      icon: <Twitter />,
      action: () => {
        const text = encodeURIComponent(
          `Mira este ${product.name} - ${product.formattedPrice}`,
        );
        const url = encodeURIComponent(window.location.href);
        window.open(
          `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
          "_blank",
        );
        setShareMessage("Abriendo Twitter...");
        setShareSnackbar(true);
      },
    },
    {
      name: "Copiar enlace",
      icon: <LinkedIn />,
      action: async () => {
        try {
          await navigator.clipboard.writeText(window.location.href);
          setShareMessage("Enlace copiado al portapapeles");
          setShareSnackbar(true);
        } catch (error) {
          console.error("Error copying link:", error);
          setShareMessage("No se pudo copiar el enlace");
          setShareSnackbar(true);
        }
      },
    },
  ];

  const handleShareOption = (option) => {
    option.action();
    handleShareClose();
  };

  useEffect(() => {
    const findProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        // 🚫 CACHÉ DESACTIVADO: siempre se busca el producto directo en la API
        // para reflejar de inmediato los cambios hechos por un administrador.
        console.log("🔍 Fetching product from API (no cache)...");

        try {
          const productBySlug = await productsService.getProductBySlug(slug);

          let normalizedProduct = productBySlug;

          if (productBySlug.object) {
            normalizedProduct = productBySlug.object;
          }

          const finalProduct = {
            ...normalizedProduct,
            features:
              normalizedProduct.features ||
              normalizedProduct.caracteristicas ||
              [],
            images: fixProductImages(normalizedProduct.images || []),
            image: fixImageUrl(normalizedProduct.image),
            category:
              normalizedProduct.category || normalizedProduct.categoria || "",
            description:
              normalizedProduct.description ||
              normalizedProduct.descripcion ||
              "",
            name: normalizedProduct.name || "",
            price: normalizedProduct.price || "",
            formattedPrice: normalizedProduct.formattedPrice || "",
            year: normalizedProduct.year || normalizedProduct.año || "",
            kilometers:
              normalizedProduct.kilometers ||
              normalizedProduct.kilometraje ||
              "",
            model: normalizedProduct.model || normalizedProduct.modelo || "",
            videos: normalizedProduct.videos || [],
          };

          setProduct(finalProduct);
        } catch (slugError) {
          console.log("🔍 Slug search failed, trying by ID...", slugError);

          const idMatch = slug.match(/(\d+)$/);
          if (idMatch) {
            const id = idMatch[1];
            const productById = await productsService.getProductById(id);
            console.log("✅ Product fetched by ID:", productById);

            let normalizedProduct = productById;
            if (productById.object) {
              normalizedProduct = productById.object;
            }

            const finalProduct = {
              ...normalizedProduct,
              features:
                normalizedProduct.features ||
                normalizedProduct.caracteristicas ||
                [],
              images: fixProductImages(normalizedProduct.images || []),
              image: fixImageUrl(normalizedProduct.image),
              category:
                normalizedProduct.category || normalizedProduct.categoria || "",
              description:
                normalizedProduct.description ||
                normalizedProduct.descripcion ||
                "",
              name: normalizedProduct.name || "",
              price: normalizedProduct.price || "",
              formattedPrice: normalizedProduct.formattedPrice || "",
              year: normalizedProduct.year || normalizedProduct.año || "",
              kilometers:
                normalizedProduct.kilometers ||
                normalizedProduct.kilometraje ||
                "",
              model: normalizedProduct.model || normalizedProduct.modelo || "",
              videos: normalizedProduct.videos || [],
            };

            setProduct(finalProduct);
          } else {
            throw new Error(
              "No se pudo encontrar el producto. El formato del enlace puede ser incorrecto.",
            );
          }
        }
      } catch (err) {
        console.error("❌ Error finding product:", err);
        setError(err.message);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      findProduct();
    }
  }, [slug]);

  // 🆕 Productos relacionados: se piden frescos a la API (sin caché) una vez
  // que se conoce el producto actual, para la sección "También podrían interesarte".
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadRelatedProducts = async () => {
      if (!product?.id) return;

      try {
        const response = await productsService.getProducts({
          page: 1,
          limit: 12,
          category: product.category || "",
          area: 1,
        });

        if (cancelled) return;

        let candidates = (response?.products || []).filter(
          (p) => p.id !== product.id,
        );

        // Si no hay otros productos de la misma categoría, se busca en general
        if (candidates.length === 0) {
          const fallbackResponse = await productsService.getProducts({
            page: 1,
            limit: 12,
            area: 1,
          });

          if (cancelled) return;

          candidates = (fallbackResponse?.products || []).filter(
            (p) => p.id !== product.id,
          );
        }

        setRelatedProducts(candidates);
      } catch (err) {
        console.warn("⚠️ No se pudieron cargar productos relacionados:", err);
        if (!cancelled) setRelatedProducts([]);
      }
    };

    loadRelatedProducts();

    return () => {
      cancelled = true;
    };
  }, [product?.id, product?.category]);

  const formatKilometers = (kilometers) => {
    if (!kilometers || kilometers === "N/A" || kilometers === "") {
      return "N/A";
    }

    const numericValue = kilometers.toString().replace(/[^\d]/g, "");

    if (!numericValue || numericValue === "0") {
      return "N/A";
    }

    const formatted = parseInt(numericValue).toLocaleString("es-CL");

    return `${formatted} km`;
  };

  const formatUsage = (product) => {
    if (product.usageDisplay) {
      return product.usageDisplay;
    }

    if (product.usage && product.usageUnit) {
      const numericValue = product.usage.toString().replace(/[^\d]/g, "");
      if (numericValue && numericValue !== "0") {
        const formatted = parseInt(numericValue).toLocaleString("es-CL");
        const unit = product.usageUnit === "hr" ? "hrs" : "km";
        return `${formatted} ${unit}`;
      }
    }

    if (product.kilometers) {
      return formatKilometers(product.kilometers);
    }

    return "N/A";
  };

  const getUsageIcon = (product) => {
    if (product.usageUnit === "hr") {
      return <CalendarToday sx={{ mr: 1, color: "primary.main" }} />;
    }
    return <Speed sx={{ mr: 1, color: "primary.main" }} />;
  };

  const getUsageLabel = (product) => {
    if (product.usageUnit === "hr") {
      return "Horómetro";
    }
    return "Kilometraje";
  };

  const extractVideoId = (url) => {
    if (!url) return null;

    if (url.includes("vimeo.com")) {
      const vimeoPatterns = [
        /(?:vimeo\.com\/)(\d+)/,
        /(?:vimeo\.com\/video\/)(\d+)/,
        /(?:player\.vimeo\.com\/video\/)(\d+)/,
      ];

      for (const pattern of vimeoPatterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
          return { id: match[1], platform: "vimeo" };
        }
      }
    } else {
      const youtubePatterns = [
        /(?:youtube\.com\/embed\/|youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
        /youtube\.com\/embed\/([^?&\n]+)/,
      ];

      for (const pattern of youtubePatterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
          return { id: match[1], platform: "youtube" };
        }
      }
    }

    return null;
  };

  const getVideoThumbnail = (videoData, url) => {
    if (!videoData) return placeholderImage;

    if (videoData.platform === "vimeo") {
      // Miniatura real vía oEmbed (ver useEffect). Placeholder mientras carga o
      // si Vimeo no la entrega (video privado sin dominio permitido, etc.).
      return vimeoThumbs[url] || placeholderImage;
    } else {
      return `https://img.youtube.com/vi/${videoData.id}/maxresdefault.jpg`;
    }
  };

  const productVideos =
    product?.videos && product.videos.length > 0
      ? product.videos
          .sort((a, b) => a.index - b.index)
          .map((video) => {
            const videoData = extractVideoId(video.url);
            return {
              type: "video",
              src: videoData?.id,
              platform: videoData?.platform || "youtube",
              url: video.url,
              thumbnail: videoData
                ? getVideoThumbnail(videoData, video.url)
                : placeholderImage,
              title: `Video ${video.index + 1} - ${product.name}`,
              alt: `Video ${video.index + 1} del producto ${product.name}`,
              originalIndex: video.index,
              id: video.id,
            };
          })
          .filter((video) => video.src)
      : [];

  const productImages =
    product?.images && product.images.length > 0
      ? product.images.map((img, index) => ({
          type: "image",
          src: img,
          thumbnail: img,
          alt: `${product.name} - Imagen ${index + 1}`,
        }))
      : [
          {
            type: "image",
            src: product?.image || placeholderImage,
            thumbnail: product?.image || placeholderImage,
            alt: product?.name,
          },
        ];

  const allMedia = [...productVideos, ...productImages];

  useSEO({
    title: product?.name,
    description: `${product?.description} Precio: ${
      product?.formattedPrice || product?.price
    }. Cotización gratis. ${product?.features.join(" • ")}.`,
    keywords: `${product?.name}, ${product?.category}, ${product?.features.join(
      ", ",
    )}`,
    image: product?.image,
    type: "product",
  });

  // 🎯 SEO: Structured Data (JSON-LD) automático para Google
  useProductStructuredData(product);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (allMedia.length <= 1) return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          setSelectedMedia((prev) =>
            prev === 0 ? allMedia.length - 1 : prev - 1,
          );
          break;
        case "ArrowRight":
          event.preventDefault();
          setSelectedMedia((prev) =>
            prev === allMedia.length - 1 ? 0 : prev + 1,
          );
          break;
        case "Escape":
          if (openMediaDialog) {
            event.preventDefault();
            setOpenMediaDialog(false);
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [allMedia.length, openMediaDialog]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Cargando producto...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h4" color="error" gutterBottom>
            Error al cargar el producto
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/catalogo")}
            sx={{ mt: 2 }}
          >
            Volver al Catálogo
          </Button>
        </Box>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h4" color="error" gutterBottom>
            Producto no encontrado
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            El producto que buscas no existe o ha sido eliminado.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/catalogo")}
            sx={{ mt: 2 }}
          >
            Volver al Catálogo
          </Button>
        </Box>
      </Container>
    );
  }

  const currentMedia = allMedia[selectedMedia];
  const isVideo = currentMedia?.type === "video";

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
          >
            Inicio
          </Link>
          <Link
            color="inherit"
            href="/catalogo"
            onClick={(e) => {
              e.preventDefault();
              navigateToCatalog();
            }}
          >
            Catálogo
          </Link>
          <Typography color="text.primary">
            {product.name.length > 25
              ? product.name.substring(0, 25) + "..."
              : product.name}
          </Typography>
        </Breadcrumbs>

        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            "&:hover": {
              backgroundColor: "primary.dark",
              color: "white",
            },
          }}
          title="Volver atrás"
        >
          <ArrowBack />
        </IconButton>
      </Box>

      <Grid container spacing={4}>
        {/* Columna izquierda - Media (Imágenes y Videos) */}
        <Grid size={{ xs: 12, md: 7 }}>
          {/* Media principal */}
          <Card
            sx={{
              mb: 2,
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 6px 24px rgba(0,0,0,0.10)",
            }}
          >
            <Box sx={{ position: "relative", backgroundColor: "#f4f6f8" }}>
              {isVideo ? (
                <YoutubeEmbed
                  videoId={currentMedia.src}
                  url={currentMedia.url}
                  platform={currentMedia.platform}
                  title={currentMedia.title}
                  height={400}
                />
              ) : (
                <SmartCardMedia
                  height="400"
                  image={currentMedia.src}
                  alt={product.name}
                  title={product.name}
                  fallback={placeholderImage}
                  sx={{
                    cursor: "pointer",
                    filter: product.is_sold
                      ? "grayscale(100%) brightness(0.8)"
                      : "none",
                  }}
                  onClick={() => !isVideo && setOpenMediaDialog(true)}
                />
              )}

              {/* 🆕 OVERLAY para productos vendidos */}
              {product.is_sold && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 1,
                    pointerEvents: "none",
                  }}
                />
              )}

              {/* 🆕 MARCA DE AGUA DIAGONAL "VENDIDO" */}
              {product.is_sold && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%) rotate(-25deg)",
                    zIndex: 2,
                    pointerEvents: "none",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      fontWeight: "bold",
                      color: "#d32f2f",
                      textShadow:
                        "0 0 30px rgba(0,0,0,0.9), 0 0 15px #d32f2f60",
                      fontSize: "5rem",
                      letterSpacing: "16px",
                      textTransform: "uppercase",
                      WebkitTextStroke: "3px white",
                      opacity: 0.95,
                    }}
                  >
                    VENDIDO
                  </Typography>
                </Box>
              )}

              {/* Controles de navegación para la imagen principal */}
              {allMedia.length > 1 && (
                <>
                  <IconButton
                    onClick={() =>
                      setSelectedMedia((prev) =>
                        prev === 0 ? allMedia.length - 1 : prev - 1,
                      )
                    }
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: 16,
                      transform: "translateY(-50%)",
                      backgroundColor: "rgba(0,0,0,0.7)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.8)",
                        transform: "translateY(-50%) scale(1.1)",
                      },
                      transition: "all 0.2s ease",
                      zIndex: 10,
                    }}
                    size="large"
                    title="Imagen anterior"
                  >
                    <KeyboardArrowLeft fontSize="large" />
                  </IconButton>

                  <IconButton
                    onClick={() =>
                      setSelectedMedia((prev) =>
                        prev === allMedia.length - 1 ? 0 : prev + 1,
                      )
                    }
                    sx={{
                      position: "absolute",
                      top: "50%",
                      right: 16,
                      transform: "translateY(-50%)",
                      backgroundColor: "rgba(0,0,0,0.7)",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.8)",
                        transform: "translateY(-50%) scale(1.1)",
                      },
                      transition: "all 0.2s ease",
                      zIndex: 10,
                    }}
                    size="large"
                    title="Imagen siguiente"
                  >
                    <KeyboardArrowRight fontSize="large" />
                  </IconButton>
                </>
              )}

              {/* Botones superpuestos */}
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  display: "flex",
                  gap: 1,
                  zIndex: 10,
                }}
              >
                {/* 🆕 Badge VENDIDO */}
                {product.is_sold && (
                  <Chip
                    label="VENDIDO"
                    sx={{
                      backgroundColor: "#d32f2f",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "1rem",
                      height: 32,
                      boxShadow: "0 4px 12px #d32f2f60",
                      animation: "soldPulse 2s ease-in-out infinite",
                      "@keyframes soldPulse": {
                        "0%, 100%": {
                          transform: "scale(1)",
                        },
                        "50%": {
                          transform: "scale(1.05)",
                        },
                      },
                    }}
                  />
                )}

                <IconButton
                  onClick={handleShareClick}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.9)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,1)",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s ease",
                  }}
                  title="Compartir producto"
                >
                  <Share />
                </IconButton>

                {
                  <FavoriteButton
                    product={product}
                    sx={{
                      backgroundColor: "rgba(0,0,0,0.7)",
                      backdropFilter: "blur(6px)",
                      border: "2px solid rgba(255,255,255,0.4)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.85)",
                        border: "2px solid rgba(255,255,255,0.6)",
                        transform: "scale(1.1)",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  />
                }
              </Box>

              {/* Botón de zoom/fullscreen */}
              {!isVideo && (
                <IconButton
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    right: 16,
                    backgroundColor: "rgba(0,0,0,0.7)",
                    color: "white",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
                  }}
                  onClick={() => setOpenMediaDialog(true)}
                >
                  <ZoomIn />
                </IconButton>
              )}

              {/* Indicador de video */}
              {isVideo && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 16,
                    backgroundColor: "rgba(0,0,0,0.7)",
                    color: "white",
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <VideoLibrary fontSize="small" />
                  <Typography variant="caption">
                    Video{" "}
                    {productVideos.findIndex(
                      (m) => m.src === currentMedia.src,
                    ) + 1}
                  </Typography>
                </Box>
              )}

              {/* Contador de multimedia (arriba-izquierda para no tapar los
                  controles del video ni los botones de compartir/favorito) */}
              {allMedia.length > 1 && (
                <Chip
                  label={`${selectedMedia + 1} / ${allMedia.length}`}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 16,
                    left: 16,
                    backgroundColor: "rgba(0,0,0,0.65)",
                    color: "white",
                    fontWeight: 600,
                    backdropFilter: "blur(4px)",
                    zIndex: 10,
                  }}
                />
              )}
            </Box>
          </Card>

          {/* Galería de miniaturas con videos */}
          <ImageList
            cols={isMobile ? 4 : 6}
            gap={5}
            rowHeight={isMobile ? 72 : 88}
            sx={{ p: 1, overflow: "hidden" }}
          >
            {allMedia.map((media, index) => {
              const active = selectedMedia === index;
              return (
                <ImageListItem
                  key={index}
                  onClick={() => setSelectedMedia(index)}
                  sx={{
                    cursor: "pointer",
                    borderRadius: 2,
                    overflow: "hidden",
                    position: "relative",
                    outline: active
                      ? "3px solid"
                      : "1px solid rgba(0,0,0,0.08)",
                    outlineColor: active ? "primary.main" : undefined,
                    outlineOffset: active ? "-1px" : 0,
                    boxShadow: active ? "0 4px 12px rgba(0,0,0,0.18)" : "none",
                    opacity: active ? 1 : 0.85,
                    transition: "all 0.2s ease",
                    filter: product.is_sold ? "grayscale(80%)" : "none",
                    "&:hover": {
                      opacity: 1,
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                    },
                  }}
                >
                  <img
                    src={media.thumbnail}
                    alt={media.alt || `${product.name} ${index + 1}`}
                    title={media.alt || `${product.name} ${index + 1}`}
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  {media.type === "video" && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.35))",
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "rgba(0,0,0,0.65)",
                          borderRadius: "50%",
                          width: 32,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backdropFilter: "blur(2px)",
                        }}
                      >
                        <PlayArrow sx={{ color: "white", fontSize: 20 }} />
                      </Box>
                    </Box>
                  )}
                </ImageListItem>
              );
            })}
          </ImageList>
        </Grid>

        {/* Columna derecha - Información principal */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper
            sx={{
              p: 3,
              height: "fit-content",
              position: "sticky",
              top: 20,
              border: product.is_sold ? "2px solid #d32f2f" : "none",
              backgroundColor: product.is_sold ? "#fafafa" : "white",
            }}
          >
            {/* 🆕 Badge VENDIDO prominente en el header */}
            {product.is_sold && (
              <Box sx={{ mb: 2, textAlign: "center" }}>
                <Chip
                  label="PRODUCTO VENDIDO"
                  sx={{
                    backgroundColor: "#d32f2f",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    height: 40,
                    px: 2,
                    boxShadow: "0 4px 12px rgba(220, 0, 78, 0.4)",
                    width: "100%",
                  }}
                />
              </Box>
            )}

            {/* Año en chip - solo mostrar si existe */}
            {product.year && (
              <Chip
                icon={<CalendarToday sx={{ fontSize: "16px" }} />}
                label={`AÑO ${product.year}`}
                color="primary"
                sx={{
                  mb: 2,
                  opacity: product.is_sold ? 0.6 : 1,
                }}
              />
            )}

            {/* Título */}
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "24px", md: "24px" },
                opacity: product.is_sold ? 0.7 : 1,
              }}
            >
              {product.name.toUpperCase()}
            </Typography>

            {/* Botón guardar - ocultar si está vendido */}
            {<FavoriteButton product={product} mode="button" />}

            {/* Información básica - solo mostrar campos con valores */}
            <Box sx={{ my: 3, opacity: product.is_sold ? 0.7 : 1 }}>
              {product.model && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Build sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="body1">
                    <strong>Modelo:</strong> {product.model}
                  </Typography>
                </Box>
              )}
              {product.usageDisplay && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  {getUsageIcon(product)}
                  <Typography variant="body1">
                    <strong>{getUsageLabel(product)}: </strong>{" "}
                    {formatUsage(product)}
                  </Typography>
                </Box>
              )}
              {/* 📍 Ubicación del producto */}
              {product.ubicacion && product.ubicacion !== "N/A" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    mb: 1,
                  }}
                >
                  <LocationOn sx={{ mr: 1, color: "#d32f2f", mt: 0.3 }} />
                  <Typography
                    variant="body1"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {product.ubicacion
                      .toLowerCase()
                      .split(", ")
                      .map((part, index, arr) => (
                        <span key={index}>
                          {index === 0 ? <strong>{part}</strong> : part}
                          {index < arr.length - 1 && ", "}
                        </span>
                      ))}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Precio destacado */}
            <Box
              sx={{
                textAlign: "center",
                mb: 4,
                p: 2,
                backgroundColor: product.is_sold ? "#e0e0e0" : "#f5f5f5",
                borderRadius: 2,
                opacity: product.is_sold ? 0.7 : 1,
              }}
            >
              {/* Con desglose de precio */}
              {hasValidPriceBreakdown && !product.is_sold ? (
                <>
                  <Typography
                    variant="h4"
                    color="primary"
                    sx={{ fontWeight: "bold" }}
                  >
                    {product.formattedPrice}
                  </Typography>
                  {/* Mostrar IVA solo si existe y no es vacío */}
                  {product.priceIVA &&
                    product.priceIVA.trim() !== "" &&
                    product.priceIVA !== "N/A" && (
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ mt: 0.5 }}
                      >
                        {hasValidPriceBreakdown
                          ? "Precio a pagar"
                          : product.priceIVA}
                      </Typography>
                    )}
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<InfoIcon />}
                    onClick={() => setOpenPriceBreakdownModal(true)}
                    sx={{
                      mt: 1.5,
                      color: "primary.main",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                      },
                    }}
                  >
                    Ver desglose de costos
                  </Button>
                </>
              ) : (
                <>
                  {/* Sin desglose - mostrar precio normal */}
                  <Typography
                    variant="h4"
                    color="primary"
                    sx={{ fontWeight: "bold" }}
                  >
                    {product.formattedPrice}
                  </Typography>
                  {/* Mostrar IVA solo si existe y no es vacío */}
                  {product.priceIVA &&
                    product.priceIVA.trim() !== "" &&
                    product.priceIVA !== "N/A" && (
                      <Typography variant="body2" color="primary">
                        {hasValidPriceBreakdown
                          ? "Precio a pagar"
                          : product.priceIVA}
                      </Typography>
                    )}
                  {product.is_sold && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#d32f2f",
                        fontWeight: "bold",
                        mt: 1,
                      }}
                    >
                      Este producto ya ha sido vendido
                    </Typography>
                  )}
                </>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block", textDecoration: "" }}
              >
                {hasLicensePlate
                  ? "No incluye gastos de transferencia y operacionales"
                  : "No incluye gastos operacionales"}
              </Typography>
            </Box>

            {/* Botones de acción */}
            {!product.is_sold && (
              <Stack spacing={2} sx={{ mb: 4 }}>
                <Typography
                  sx={{
                    textAlign: "center",
                    color: "text.secondary",
                    whiteSpace: "pre-line",
                  }}
                  variant="span"
                  gutterBottom
                >
                  {/* Debería aparecer un texto abajo del otro */}
                  Horario de atención:
                  {`\n${CONTACT_INFO.schedule.weekdays}.\n${CONTACT_INFO.schedule.friday}.\n${CONTACT_INFO.schedule.weekend}.`}
                </Typography>
                <Tooltip
                  title={
                    user
                      ? "Cotizar producto"
                      : "Debes iniciar sesión para cotizar"
                  }
                >
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<QuoteIcon />}
                    onClick={handleQuoteClick}
                    fullWidth
                    sx={{
                      py: 1.5,
                      boxShadow: "0 0 10px rgba(25, 118, 210, 0.3)",
                      animation:
                        "subtle-glow 2s ease-in-out infinite alternate",
                      "@keyframes subtle-glow": {
                        "0%": {
                          boxShadow: "0 0 10px rgba(25, 118, 210, 0.3)",
                        },
                        "100%": {
                          boxShadow: "0 0 20px rgba(25, 118, 210, 0.5)",
                        },
                      },
                      "&:hover": {
                        boxShadow: "0 0 25px rgba(25, 118, 210, 0.6)",
                        animation: "none",
                      },
                    }}
                  >
                    <Typography fontWeight={"bold"}>
                      SOLICITAR COTIZACIÓN
                    </Typography>
                  </Button>
                </Tooltip>
              </Stack>
            )}

            {/* Mensaje alternativo si está vendido */}
            {product.is_sold && (
              <Box sx={{ mb: 4, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Este producto ya no está disponible
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={navigateToCatalog}
                  sx={{
                    backgroundColor: "#9e9e9e",
                    "&:hover": {
                      backgroundColor: "#757575",
                    },
                  }}
                >
                  Ver Productos Disponibles
                </Button>
              </Box>
            )}

            {/* Información de contacto */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" fontWeight={"bold"} gutterBottom>
              CONTACTOS
            </Typography>
            <Box
              sx={{
                mb: 3,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                flexWrap: "wrap",
                alignContent: "flex-start",
                columnGap: 2,
                rowGap: 2,
              }}
            >
              {CONTACT_INFO.phones.map((phone, index) => (
                <Box
                  key={index + "-" + phone}
                  sx={{ width: isTablet || isMobile ? "100%" : "48%" }}
                >
                  <Typography variant="subtitle2">{phone.label}</Typography>
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    startIcon={<Phone />}
                    href={`tel:${phone.number.replace(/\s/g, "")}`}
                    sx={{
                      backgroundColor: "white",
                      color: "primary.main",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        textAlign={"center"}
                        sx={{
                          textAlign: "center",
                          justifyContent: "center",
                          alignContent: "center",
                        }}
                      >
                        {phone.number}{" "}
                      </Typography>
                    </Box>
                    <CopyButton
                      text={phone.number}
                      id={`phone-${index}`}
                      showFeedback={false}
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                    />
                  </Button>
                </Box>
              ))}
            </Box>
            <Divider sx={{ mb: 3 }} />
            {CONTACT_INFO.emails.map((emailInfo, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  {emailInfo.label}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Email />}
                    href={`mailto:${emailInfo.email}`}
                    sx={{ flexGrow: 1 }}
                  >
                    <Typography fontWeight={"bold"}>
                      {emailInfo.email}
                    </Typography>
                  </Button>
                  <CopyButton
                    text={emailInfo.email}
                    showFeedback={false}
                    id={`email-${index}`}
                  />
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* Sección de tabs con información detallada */}
      <Box sx={{ mt: 6 }}>
        <Paper>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Especificaciones" />
            <Tab label="Ubicación" />
          </Tabs>

          {/* Detalles */}
          <TabPanel value={tabValue} index={-1}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Descripción
              </Typography>
              <Typography variant="body1" paragraph>
                {product.description}
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Características Destacadas
              </Typography>
              <Grid container spacing={1}>
                {product.features.map((feature, index) => (
                  <Grid key={index}>
                    <Chip
                      icon={<CheckCircle />}
                      label={feature}
                      variant="outlined"
                      color="primary"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </TabPanel>

          {/* Especificaciones */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ p: 3 }}>
              {product.details ? (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Especificaciones del Producto
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(product.details)
                      .filter(
                        ([, value]) => value && value.toString().trim() !== "",
                      )
                      .map(([key, value]) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: "#f9f9f9",
                              borderRadius: 1,
                              height: "100%",
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                textTransform: "capitalize",
                                fontWeight: "medium",
                                mb: 0.5,
                              }}
                            >
                              {key.replace(/_/g, " ")}
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight="medium"
                              sx={{
                                wordBreak: "break-word",
                              }}
                            >
                              {value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              ) : (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Información General
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      { key: "Marca", value: product.brand },
                      { key: "Modelo", value: product.model },
                      { key: "Año", value: product.year },
                      {
                        key: product.usageDisplay,
                        value: product.usageDisplay,
                      },
                      { key: "Motor", value: product.motor },
                      { key: "Color", value: product.color },
                      { key: "Estado", value: product.estado },
                      { key: "Categoría", value: product.category },
                      { key: "Capacidad", value: product.capacidad },
                      { key: "Transmisión", value: product.transmision },
                      { key: "Combustible", value: product.combustible },
                      { key: "Cilindros", value: product.cilindros },
                      { key: "Puertas", value: product.puertas },
                      { key: "Asientos", value: product.asientos },
                    ]
                      .filter(
                        (item) =>
                          item.value &&
                          item.value.toString().trim() !== "" &&
                          item.value !== "N/A",
                      )
                      .map(({ key, value }) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                          <Box
                            sx={{
                              p: 2,
                              backgroundColor: "#f9f9f9",
                              borderRadius: 1,
                              height: "100%",
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontWeight: "medium",
                                mb: 0.5,
                              }}
                            >
                              {key}
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight="medium"
                              sx={{
                                wordBreak: "break-word",
                              }}
                            >
                              {value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                  </Grid>

                  {[
                    { key: "Marca", value: product.brand },
                    { key: "Modelo", value: product.model },
                    { key: "Año", value: product.year },
                    { key: "Kilometraje", value: product.kilometers },
                    { key: "Motor", value: product.motor },
                    { key: "Color", value: product.color },
                    { key: "Estado", value: product.estado },
                    { key: "Categoría", value: product.category },
                    { key: "Capacidad", value: product.capacidad },
                    { key: "Transmisión", value: product.transmision },
                    { key: "Combustible", value: product.combustible },
                    { key: "Cilindros", value: product.cilindros },
                    { key: "Puertas", value: product.puertas },
                    { key: "Asientos", value: product.asientos },
                  ].filter(
                    (item) =>
                      item.value &&
                      item.value.toString().trim() !== "" &&
                      item.value !== "N/A",
                  ).length === 0 && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No hay especificaciones técnicas disponibles para este
                        producto.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {product.description && product.description.trim() !== "" && (
                <Box
                  sx={{
                    textAlign: "center",
                    mt: 3,
                    pt: 3,
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setOpenDetailsModal(true)}
                    startIcon={<FileIcon />}
                    size="large"
                    sx={{ px: 4 }}
                  >
                    Ver más detalles
                  </Button>
                </Box>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Ubicación
              </Typography>

              {/* 📍 Ubicación del producto (si existe) */}
              {product.ubicacion &&
                product.ubicacion !== "N/A" &&
                product.ubicacion !== "Chile" && (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      backgroundColor: "#fff3e0",
                      borderRadius: 2,
                      border: "1px solid #ffcc80",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      <LocationOn
                        sx={{ color: "#e65100", fontSize: 24, mt: 0.3 }}
                      />
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: "#e65100", fontWeight: "bold", mb: 0.5 }}
                        >
                          Ubicación del producto
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ textTransform: "capitalize" }}
                        >
                          {product.ubicacion
                            .toLowerCase()
                            .split(", ")
                            .map((part, index, arr) => (
                              <span key={index}>
                                {index === 0 ? <strong>{part}</strong> : part}
                                {index < arr.length - 1 && ", "}
                              </span>
                            ))}
                        </Typography>
                        {product.locationAddress && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5, textTransform: "capitalize" }}
                          >
                            {product.locationAddress.toLowerCase()}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}

              {/* 📍 Punto de venta Tattersall */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: "#e3f2fd",
                  borderRadius: 2,
                  border: "1px solid #90caf9",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <LocationOn
                    sx={{ color: "#1565c0", fontSize: 24, mt: 0.3 }}
                  />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "#1565c0", fontWeight: "bold", mb: 0.5 }}
                    >
                      Punto de venta Tattersall Venta Usados
                    </Typography>
                    <Typography variant="body1">
                      <strong>Camino Longitudinal</strong>, Ruta 5 2535, Buin
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      Región Metropolitana de Santiago
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Mapa de Google */}
              <Box
                sx={{
                  backgroundColor: "#f0f0f0",
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <iframe
                  src={locations[0].embedRef}
                  width="100%"
                  height="350"
                  style={{ border: 0, display: "block" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación Tattersall Venta Usados"
                ></iframe>
              </Box>

              {/* Botón para abrir en Google Maps */}
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Button
                  variant="outlined"
                  startIcon={<MapIcon />}
                  href={
                    locations[0].mapsRef ||
                    "https://maps.google.com/?q=Tattersall+Venta+Usados+Buin"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Abrir en Google Maps
                </Button>
              </Box>
            </Box>
          </TabPanel>
        </Paper>
      </Box>

      <Box sx={{ mt: 6 }}>
        <Typography
          variant="h4"
          gutterBottom
          textAlign="center"
          color="primary"
        >
          TAMBIÉN PODRÍAN INTERESARTE
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {relatedProducts
            .filter(
              (p) => p.id !== product.id && p.category === product.category,
            )
            .slice(0, 4)
            .map((relatedProduct) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={relatedProduct.id}>
                <Card
                  sx={{
                    height: "100%",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      transition: "all 0.3s ease",
                    },
                  }}
                  onClick={() => navigate(`/producto/${relatedProduct.slug}`)}
                >
                  <SmartCardMedia
                    height="200"
                    image={
                      relatedProduct.image ||
                      relatedProduct.images?.[0] ||
                      placeholderImage
                    }
                    alt={relatedProduct.name}
                    title={relatedProduct.name}
                    fallback={placeholderImage}
                  />
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {relatedProduct.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2">
                        {relatedProduct.year
                          ? `Año ${relatedProduct.year}`
                          : relatedProduct.category}
                      </Typography>
                      <Typography variant="body2">
                        {relatedProduct.condition || relatedProduct.estado}
                      </Typography>
                    </Box>
                    <Typography variant="h6" color="primary" gutterBottom>
                      {relatedProduct.formattedPrice}
                    </Typography>
                    <Button variant="contained" fullWidth size="small">
                      SABER MÁS
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
        </Grid>

        {relatedProducts.filter(
          (p) => p.id !== product.id && p.category === product.category,
        ).length === 0 && (
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {relatedProducts
              .filter((p) => p.id !== product.id)
              .slice(0, 4)
              .map((relatedProduct) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={relatedProduct.id}>
                  <Card
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        transition: "all 0.3s ease",
                      },
                    }}
                    onClick={() => navigate(`/producto/${relatedProduct.slug}`)}
                  >
                    <SmartCardMedia
                      height="200"
                      image={
                        relatedProduct.image ||
                        relatedProduct.images?.[0] ||
                        placeholderImage
                      }
                      alt={relatedProduct.name}
                      title={relatedProduct.name}
                      fallback={placeholderImage}
                    />
                    <Box sx={{ p: 2 }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {relatedProduct.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          {relatedProduct.year
                            ? `Año ${relatedProduct.year}`
                            : relatedProduct.category}
                        </Typography>
                        <Typography variant="body2">
                          {relatedProduct.condition || relatedProduct.estado}
                        </Typography>
                      </Box>
                      <Typography variant="h6" color="primary" gutterBottom>
                        {relatedProduct.formattedPrice}
                      </Typography>
                      <Button variant="contained" fullWidth size="small">
                        SABER MÁS
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
          </Grid>
        )}
      </Box>

      <Box sx={{ textAlign: "center", my: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<Inventory />}
          onClick={navigateToCatalog}
          sx={{ px: 4 }}
        >
          Ver Más Productos
        </Button>
      </Box>

      <Dialog
        open={openMediaDialog}
        onClose={() => setOpenMediaDialog(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            position: "relative",
            backgroundColor: "black",
          }}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "rgba(255,255,255,0.1)",
              color: "white",
              zIndex: 1000,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
            }}
            onClick={() => setOpenMediaDialog(false)}
          >
            <Close />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "80vh",
              width: "100%",
              position: "relative",
            }}
          >
            {currentMedia?.type === "video" ? (
              <YoutubeEmbed
                videoId={currentMedia.src}
                url={currentMedia.url}
                platform={currentMedia.platform}
                title={currentMedia.title}
                width="100%"
                height="80vh"
                autoplay={true}
              />
            ) : (
              <img
                src={currentMedia?.src}
                alt={product.name}
                style={{
                  maxWidth: "100%",
                  maxHeight: "90vh",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>

          {allMedia.length > 1 && (
            <>
              <IconButton
                onClick={() =>
                  setSelectedMedia((prev) =>
                    prev === 0 ? allMedia.length - 1 : prev - 1,
                  )
                }
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: 16,
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                    transform: "translateY(-50%) scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                  zIndex: 10,
                }}
                size="large"
                title="Imagen anterior"
              >
                <KeyboardArrowLeft fontSize="large" />
              </IconButton>

              <IconButton
                onClick={() =>
                  setSelectedMedia((prev) =>
                    prev === allMedia.length - 1 ? 0 : prev + 1,
                  )
                }
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 16,
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                    transform: "translateY(-50%) scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                  zIndex: 10,
                }}
                size="large"
                title="Imagen siguiente"
              >
                <KeyboardArrowRight fontSize="large" />
              </IconButton>

              <Box
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "rgba(0,0,0,0.7)",
                  color: "white",
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="caption">
                  {selectedMedia + 1} / {allMedia.length}
                </Typography>
                {currentMedia?.type === "video" && (
                  <VideoLibrary fontSize="small" />
                )}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={shareMenuAnchor}
        open={Boolean(shareMenuAnchor)}
        onClose={handleShareClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {shareOptions.map((option) => (
          <MenuItem
            key={option.name}
            onClick={() => handleShareOption(option)}
            sx={{ minWidth: 150 }}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText>{option.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      <ContactForm
        open={openContactForm}
        onClose={() => setOpenContactForm(false)}
        product={product}
      />
      <QuoteForm
        open={openQuoteForm}
        onClose={() => setOpenQuoteForm(false)}
        product={product}
      />

      <Snackbar
        open={shareSnackbar}
        autoHideDuration={3000}
        onClose={() => setShareSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShareSnackbar(false)}
          severity="info"
          variant="filled"
        >
          {shareMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDetailsModal}
        onClose={() => setOpenDetailsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" gutterBottom>
              Detalles del Producto
            </Typography>
            <IconButton
              onClick={() => setOpenDetailsModal(false)}
              sx={{ color: "text.secondary" }}
            >
              <Close />
            </IconButton>
          </Box>

          <Typography variant="h6" gutterBottom>
            Descripción
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
            {product.description}
          </Typography>

          {product.features && product.features.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Características Destacadas
              </Typography>
              <Grid container spacing={1}>
                {product.features.map((feature, index) => (
                  <Grid key={index}>
                    <Chip
                      icon={<CheckCircle />}
                      label={feature}
                      variant="outlined"
                      color="primary"
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>
      </Dialog>

      <LoginModal
        open={openLoginModal}
        onClose={() => setOpenLoginModal(false)}
      />

      {/* ⭐ NUEVO: Modal de Desglose de Precios */}
      <Dialog
        open={openPriceBreakdownModal}
        onClose={() => setOpenPriceBreakdownModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ReceiptLong color="primary" />
              <Typography variant="h5" fontWeight="bold">
                Desglose de Costos
              </Typography>
            </Box>
            <IconButton
              onClick={() => setOpenPriceBreakdownModal(false)}
              sx={{ color: "text.secondary" }}
            >
              <Close />
            </IconButton>
          </Box>

          {hasValidPriceBreakdown && (
            <Box>
              {/* Desglose idéntico al de la cotización final (helper compartido) */}
              {buildPriceBreakdownRows(product.priceBreakdown).map((row) => (
                <Box
                  key={row.key}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <Typography variant="body1">{row.label}</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    ${Number(row.value || 0).toLocaleString("es-CL")}
                  </Typography>
                </Box>
              ))}

              {/* TOTAL A PAGAR */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 2,
                  mt: 2,
                  backgroundColor: "#e3f2fd",
                  borderRadius: 1,
                  px: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold" color="primary">
                  TOTAL A PAGAR:
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  $
                  {Number(
                    product.priceBreakdown.totalPrice || product.price || 0,
                  ).toLocaleString("es-CL")}
                </Typography>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, textAlign: "center" }}
              >
                Este es el precio final que incluye todos los costos asociados
              </Typography>
            </Box>
          )}
        </Box>
      </Dialog>
    </Container>
  );
};

export default ProductDetail;
